import { Dropdown, Image, Space, Tag, Tooltip } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton/Button";
import EyeIcon from "../../../assets/icons/eye-icon.svg";
import { formatCurrency, disputeAgeCalculation, } from "../../../helpers/helpers";
import { formatPaymentDateTime } from "../helpers";
import DangerButton from "../../../components/CustomButton/DangerButton";

const CHANNEL_TAG_COLORS = [
  { match: "NEFT", color: "geekblue" },
  { match: "RTGS", color: "blue" },
  { match: "IMPS", color: "cyan" },
  { match: "ESCROW", color: "purple" },
  { match: "WALLET", color: "purple" },
  { match: "UPI", color: "teal" },
];

const STATUS_TAG_RULES = [
  {
    keywords: ["success", "completed", "approved", "settled", "active"],
    config: { color: "success", className: "payment-status-tag status-success" },
  },
  {
    keywords: ["pending", "processing", "initiated", "in progress"],
    config: { color: "warning", className: "payment-status-tag status-pending" },
    colorOverride: (normalized) =>
      normalized.includes("processing") ? "processing" : "warning",
  },
  {
    keywords: ["fail", "reject", "declin", "suspicious", "error"],
    config: { color: "error", className: "payment-status-tag status-failed" },
  },
];

export const getChannelTagColor = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) return "default";
  const found = CHANNEL_TAG_COLORS.find(({ match }) =>
    normalized.includes(match)
  );
  return found?.color || "blue";
};

export const getStatusTagConfig = (statusValue) => {
  const normalized = String(statusValue || "").trim().toLowerCase();
  for (const rule of STATUS_TAG_RULES) {
    if (!rule.keywords.some((keyword) => normalized.includes(keyword))) continue;
    if (rule.colorOverride) {
      return { ...rule.config, color: rule.colorOverride(normalized) };
    }
    return rule.config;
  }
  return { color: "default", className: "payment-status-tag status-default" };
};

const getTxnKey = (tabIndex) => {
  if (tabIndex === 1) return "appTransactionId";
  if (tabIndex === 5) return "transactionId";
  return "txnId";
};

const resolveStatusLabel = (raw) => {
  if (Array.isArray(raw)) return raw[0]?.statusName;
  if (raw && typeof raw === "object") return raw.statusName;
  return raw;
};

const ellipsisWithTooltip = (value, className) => {
  if (!value) return "N/A";
  return (
    <Tooltip title={value} placement="top">
      <span className={`${className} table-ellipsis-cell`}>{value}</span>
    </Tooltip>
  );
};

export const getPaymentColumns = ({
  onView,
  tabIndex,
  onApprove,
  onReject,
  onMarkDuplicate,
  canMarkDuplicate,
}) => {
  const txnKey = getTxnKey(tabIndex);

  const columns = [
    {
      title: "Transaction Id",
      dataIndex: txnKey,
      key: txnKey,
      width: 190,
      align: "center",
      render: (text, record) => {
        const resolvedId = text ?? record?.[txnKey];
        if (!resolvedId) return "N/A";
        return (
          <div className="payment-txn-id-cell">
            <span className="payment-txn-id-text">{resolvedId}</span>
          </div>
        );
      },
    },
    {
      title: "Transaction Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      align: "center",
      render: (text) => {
        const formatted = formatPaymentDateTime(text);
        if (!formatted) return "N/A";
        const [datePart, ...timeRest] = String(formatted).split(" ");
        const timePart = timeRest.join(" ");
        return (
          <div className="payment-datetime-cell">
            <span className="payment-date-line">{datePart}</span>
            {timePart ? (
              <span className="payment-time-line">{timePart}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Transaction Type",
      dataIndex: "transactionType",
      key: "transactionType",
      width: 160,
      align: "center",
      render: (text, record) => {
        const rawType = text ?? record?.transactionType;
        const normalizedType = String(rawType || "").toLowerCase();
        const isCreditOrDebit =
          normalizedType === "credit" || normalizedType === "debit";
        const primaryTagText =
          record?.txnChannel || (!isCreditOrDebit ? rawType : null);

        return (
          <Space size={4} wrap className="payment-type-tags-wrap">
            {primaryTagText ? (
              <Tag
                className="payment-type-tag"
                color={getChannelTagColor(primaryTagText)}
              >
                {String(primaryTagText).toUpperCase()}
              </Tag>
            ) : (
              <span>N/A</span>
            )}
            {tabIndex === 1 && record?.isDuplicate && (
              <Tag color="orange" className="payment-duplicate-tag">
                Duplicate
              </Tag>
            )}
          </Space>
        );
      },
    },
    ...(tabIndex !== 5
      ? [
        {
          title: "Purpose",
          dataIndex: "purpose",
          key: "purpose",
          width: 190,
          align: "center",
          render: (text, record) =>
            ellipsisWithTooltip(
              text ?? record?.purpose,
              "payment-purpose-cell"
            ),
        },
      ]
      : []),
    ...(tabIndex === 1
      ? [
        {
          title: "Deal Type",
          dataIndex: "dealType",
          key: "dealType",
          width: 160,
          align: "center",
          render: (text) =>
            text ? (
              <Tag className="payment-deal-type-tag" color="default">
                {text}
              </Tag>
            ) : (
              "-"
            ),
        },
      ]
      : []),
    {
      title: "UTR",
      dataIndex: "utr",
      key: "utr",
      width: 180,
      align: "center",
      render: (text) => ellipsisWithTooltip(text, "payment-utr-text"),
    },
    {
      title: "Beneficiary Account Number",
      dataIndex: tabIndex === 5 ? "beneficiaryAccNumber" : "beneficiaryAccNum",
      key: tabIndex === 5 ? "beneficiaryAccNumber" : "beneficiaryAccNum",
      width: 200,
      align: "center",
      render: (text, record) => {
        const acc =
          text ?? record?.beneficiaryAccNum ?? record?.beneficiaryAccNumber;
        const partyName = record?.beneficiaryName || record?.counterpartyName;
        if (!acc && !partyName) return "N/A";
        const displayValue =
          partyName && acc ? `${partyName} (${acc})` : acc || partyName;
        return ellipsisWithTooltip(displayValue, "payment-counterparty-cell");
      },
    },
    ...(tabIndex !== 1
      ? [
        {
          title: "Remitter Account Number",
          dataIndex: tabIndex === 5 ? "remitterAccNumber" : "remitterAccNum",
          key: tabIndex === 5 ? "remitterAccNumber" : "remitterAccNum",
          width: 190,
          align: "center",
          render: (text) =>
            ellipsisWithTooltip(text, "payment-counterparty-cell"),
        },
      ]
      : []),
    {
      title: "Transaction Amount",
      dataIndex: "amount",
      key: "amount",
      width: 170,
      align: "center",
      render: (text, record) => {
        if (text == null) return "N/A";
        const isDebit =
          String(record?.transactionType || "").toLowerCase() === "debit";
        return (
          <span
            className={`payment-amount-badge ${isDebit ? "is-debit" : "is-credit"}`}
          >
            {`${isDebit ? "-" : "+"}${formatCurrency(text)}`}
          </span>
        );
      },
    },
    ...(tabIndex === 1 || tabIndex === 5 || tabIndex === 4
      ? [
        {
          title: "Status",
          dataIndex: "statusName",
          key: "statusName",
          width: 150,
          align: "center",
          render: (text, record) => {
            const resolved = resolveStatusLabel(
              text ?? record?.statusName ?? record?.status
            );
            if (!resolved) return "N/A";
            const { color, className } = getStatusTagConfig(resolved);
            return (
              <Tag color={color} className={className}>
                {resolved}
              </Tag>
            );
          },
        },
      ]
      : []),
    ...(tabIndex === 5
      ? [
        {
          title: "Remarks",
          dataIndex: "suspicionReason",
          key: "suspicionReason",
          width: 190,
          align: "center",
          render: (text) => text || "N/A",
        },
      ]
      : []),
    ...(tabIndex !== 5
      ? [
        {
          title: "Actions",
          key: "actions",
          width: 100,
          align: "center",
          render: (_, record) => {
            const targetTxnId =
              tabIndex === 1 ? record?.appTransactionId : record?.txnId;
            const canDuplicateRow =
              tabIndex === 1 && canMarkDuplicate && !record?.isDuplicate;

            if (tabIndex === 4) {
              return (
                <Space size="small">
                  <CustomButton
                    color="primary"
                    variant="outlined"
                    onClick={() => onApprove?.(record)}
                  >
                    Approve
                  </CustomButton>
                  <DangerButton onClick={() => onReject?.(record)}>
                    Reject
                  </DangerButton>
                </Space>
              );
            }

            return (
              <Space size={6} className="payment-actions-cell">
                <Tooltip title="View Details">
                  <CustomButton
                    type="text"
                    className="payment-view-action-btn"
                    icon={<Image src={EyeIcon} alt="View" preview={false} />}
                    onClick={() => onView?.(targetTxnId)}
                  />
                </Tooltip>
                {canDuplicateRow && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "markDuplicate",
                          label: "Mark as Duplicate",
                          onClick: () => onMarkDuplicate?.(record),
                        },
                      ],
                    }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <MoreOutlined className="payment-more-actions-trigger" />
                  </Dropdown>
                )}
              </Space>
            );
          },
        },
      ]
      : []),
  ];
  return columns;
};

export const getDisputeColumns = ({
  onEscalate,
  onResolve,
  onView,
  showEscalate,
  showResolve,
}) => {
  const columns = [
    {
      title: "Dispute ID",
      dataIndex: "disputeId",
      key: "disputeId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Dispute Amount",
      dataIndex: "disputeAmount",
      key: "disputeAmount",
      width: 180,
      align: "right",
      render: (text) => formatCurrency(text),
    },
    {
      title: "Dispute Age",
      dataIndex: "disputeDate",
      key: "disputeDate",
      width: 180,
      render: (text) => `${disputeAgeCalculation(text)} days`,
    },
    {
      title: "Dispute Reason",
      dataIndex: "disputeReasonDesc",
      key: "disputeReasonDesc",
      width: 200,
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "disputeStatusDesc",
      key: "disputeStatusDesc",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Actions",
      key: "actions",
      width: 300,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {showEscalate && record.disputeStatusId !== "DST1000001" && (
            <CustomButton
              type="primary"
              onClick={() => onEscalate?.(record.disputeId)}
            >
              Escalate
            </CustomButton>
          )}
          {showResolve && (
            <CustomButton
              color={
                record.disputeStatusId === "DST1000001" ? "green" : "primary"
              }
              variant="solid"
              onClick={() => {
                if (record.disputeStatusId === "DST1000001") return;
                onResolve?.(record);
              }}
            >
              {record.disputeStatusId === "DST1000001" ? "Resolved" : "Resolve"}
            </CustomButton>
          )}

          <CustomButton
            color="primary"
            variant="outlined"
            onClick={() => onView?.(record.disputeId)}
          >
            View Details
          </CustomButton>
        </Space>
      ),
    },
  ];

  return columns;
};

export const getEscalationCommentColumns = () => {
  const columns = [
    {
      title: "Comment ID",
      dataIndex: "escalationCommentId",
      key: "escalationCommentId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Comment Details",
      dataIndex: "escalationComment",
      key: "escalationComment",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};

export const getDisputeCommentColumns = () => {
  const columns = [
    {
      title: "Comment ID",
      dataIndex: "disputeCommentId",
      key: "disputeCommentId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Comment Details",
      dataIndex: "disputeComment",
      key: "disputeComment",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};

export const getDisputeRelTxnColumns = () => {
  const columns = [
    {
      title: "Transaction Date (Created Date)",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 200,
      fixed: "left",
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Transaction Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
      align: "right",
      render: (text) => formatCurrency(text),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};
